import { useState } from "react";
import { prefersReducedMotion } from "@/components/system";

/* ═══════════════════════════════════════════════════════════════════════════
   HeroFilm.

   The slot for a generated cinematic loop (Higgsfield, Veo, Kling — anything
   that outputs a video file). Drop the assets at these paths and the hero
   picks them up on the next build, no code change:

     public/hero/hero-loop.webm    VP9, the preferred source
     public/hero/hero-loop.mp4     H.264 fallback
     public/hero/hero-poster.jpg   first frame, also the phone/still image

   Behaviour:
   · Desktop with motion allowed: autoplaying, muted, looping video.
   · Phones and reduced motion: the poster only. A hero video on mobile data
     is the single largest thing on the page, and reduced motion means still.
   · Files missing: renders nothing and reports it, so the hex mesh underneath
     stays as the scene. The site never depends on the film existing.

   The mask keeps the left half of the frame quiet so the headline sits on a
   near-black plane whatever the film is doing; the brief asks for the subject
   on the right for the same reason. */
export function HeroFilm({ onReady, onMissing }) {
  const [missing, setMissing] = useState(false);
  const still =
    prefersReducedMotion() ||
    (typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches);

  if (missing) return null;

  const fail = () => { setMissing(true); onMissing?.(); };
  const ready = () => onReady?.();

  if (still) {
    return (
      <img
        className="hero-film"
        src="/hero/hero-poster.jpg"
        alt=""
        decoding="async"
        onLoad={ready}
        onError={fail}
      />
    );
  }

  return (
    <video
      className="hero-film"
      autoPlay
      muted
      loop
      playsInline
      preload="metadata"
      poster="/hero/hero-poster.jpg"
      onCanPlay={ready}
    >
      <source src="/hero/hero-loop.webm" type="video/webm" />
      {/* The error for "no source could be loaded" fires on the LAST source
          element, not on the video, so the handler lives here. */}
      <source src="/hero/hero-loop.mp4" type="video/mp4" onError={fail} />
    </video>
  );
}

export default HeroFilm;
